import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Play, BookOpen, ArrowRight, Loader2, ExternalLink, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { getContent } from '../content/siteContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Features = () => {
  const { language } = useLanguage();
  const content = getContent(language);
  const { features } = content;
  
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const watchContent = language === 'es' ? [
    {
      title: 'Detrás de la Marca: Construyendo Ciano Gourmet',
      description: 'Una mirada al interior de cómo construimos una marca de catering premium desde cero.',
      duration: '12:34',
      thumbnail: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600',
    },
    {
      title: 'Masterclass de Diseño de Eventos - Preview',
      description: 'Principios clave de la masterclass completa sobre crear experiencias inolvidables.',
      duration: '8:22',
      thumbnail: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600',
    },
    {
      title: 'El Método Evoke Explicado',
      description: 'Entendiendo el framework detrás de cada experiencia que diseñamos.',
      duration: '15:47',
      thumbnail: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=600',
    },
  ] : [
    {
      title: 'Behind the Brand: Building Ciano Gourmet',
      description: 'A look inside how we built a premium catering brand from the ground up.',
      duration: '12:34',
      thumbnail: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600',
    },
    {
      title: 'Event Design Masterclass Preview',
      description: 'Key principles from the full masterclass on creating unforgettable experiences.',
      duration: '8:22',
      thumbnail: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600',
    },
    {
      title: 'The Evoke Method Explained',
      description: 'Understanding the framework behind every experience we design.',
      duration: '15:47',
      thumbnail: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=600',
    },
  ];

  const readContent = language === 'es' ? [
    {
      title: 'Por Qué la Hospitalidad es el Sistema Operativo Definitivo',
      excerpt: 'Lecciones de 10 años construyendo experiencias. Cómo el pensamiento de hospitalidad transforma cualquier negocio.',
      readTime: '8 min de lectura',
      category: 'Diseño de Experiencias',
    },
    {
      title: 'Construyendo Presencia de Marca Auténtica',
      excerpt: 'Cómo crear conexión significativa con tu audiencia más allá de los algoritmos.',
      readTime: '6 min de lectura',
      category: 'Estrategia de Marca',
    },
    {
      title: 'Administrando Cuatro Ventures Sin Quemarte',
      excerpt: 'Los sistemas, límites y estructuras de equipo que lo hacen posible.',
      readTime: '10 min de lectura',
      category: 'Sistemas de Fundadores',
    },
    {
      title: 'Las Cinco Capas Sensoriales del Diseño de Eventos',
      excerpt: 'Cómo involucrar vista, sonido, gusto, tacto y olfato para experiencias inolvidables.',
      readTime: '7 min de lectura',
      category: 'Diseño de Experiencias',
    },
  ] : [
    {
      title: 'Why Hospitality is the Ultimate Operating System',
      excerpt: 'Lessons from 10 years of building experiences. How hospitality thinking transforms every business.',
      readTime: '8 min read',
      category: 'Experience Design',
    },
    {
      title: 'Building Authentic Brand Presence',
      excerpt: 'How to create meaningful connection with your audience beyond the algorithms.',
      readTime: '6 min read',
      category: 'Brand Strategy',
    },
    {
      title: 'Running Four Ventures Without Burning Out',
      excerpt: 'The systems, boundaries, and team structures that make it possible.',
      readTime: '10 min read',
      category: 'Founder Systems',
    },
    {
      title: 'The Five Sensory Layers of Event Design',
      excerpt: 'How to engage sight, sound, taste, touch, and scent for unforgettable experiences.',
      readTime: '7 min read',
      category: 'Experience Design',
    },
  ];

  const pressFeatures = language === 'es' ? [
    {
      publication: 'Hospitality Design Magazine',
      title: 'El Auge del Diseño de Eventos Centrado en la Experiencia',
      date: 'Marzo 2025',
      type: 'Artículo',
      url: '#',
    },
    {
      publication: 'Forbes Lifestyle',
      title: 'Cómo Esta Fundadora Construyó Cuatro Ventures Sin Quemarse',
      date: 'Febrero 2025',
      type: 'Entrevista',
      url: '#',
    },
    {
      publication: 'Business Insider',
      title: 'Los Sistemas Operativos Detrás de los Eventos de Lujo',
      date: 'Enero 2025',
      type: 'Perfil',
      url: '#',
    },
  ] : [
    {
      publication: 'Hospitality Design Magazine',
      title: 'The Rise of Experience-First Event Design',
      date: 'March 2025',
      type: 'Feature',
      url: '#',
    },
    {
      publication: 'Forbes Lifestyle',
      title: 'How This Founder Built Four Ventures Without Burning Out',
      date: 'February 2025',
      type: 'Interview',
      url: '#',
    },
    {
      publication: 'Business Insider',
      title: 'The Operating Systems Behind Luxury Events',
      date: 'January 2025',
      type: 'Profile',
      url: '#',
    },
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      if (response.ok) {
        toast.success(
          language === 'es' ? '¡Suscrito!' : 'Subscribed!',
          { description: language === 'es' ? 'Gracias por suscribirte.' : 'Thanks for subscribing.' }
        );
        setEmail('');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al suscribirse' : 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="bg-white pt-32 pb-20">
      {/* Hero */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-7xl font-bold text-stone-900 mb-6">
            {features.hero.headline}
          </h1>
          <p className="text-2xl md:text-3xl text-stone-900 mb-6 leading-tight">
            {features.hero.subheadline}
          </p>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            {features.hero.description}
          </p>
        </div>
      </section>

      {/* Selected Moments - Replaces Press Kit */}
      <section className="section-padding px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-stone-900 text-white p-12 rounded-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Camera className="w-8 h-8 text-terracota" />
              <h2 className="font-serif text-3xl font-bold">
                {language === 'es' ? 'Momentos Selectos' : 'Selected Moments'}
              </h2>
            </div>
            <p className="text-lg mb-8 opacity-90 max-w-2xl">
              {language === 'es' 
                ? 'Activos de storytelling en cámara para lanzamientos, gifting, momentos de campaña—y estilo de vida de fundadora.'
                : 'On-camera storytelling assets for launches, gifting, campaign moments—and founder lifestyle.'}
            </p>
            <Link to="/selected-moments">
              <Button className="bg-terracota text-white hover:bg-white hover:text-stone-900 px-8 py-3 rounded-full transition-all" data-testid="selected-moments-btn">
                {language === 'es' ? 'Ver Momentos' : 'See Moments'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Watch Section */}
      <section className="section-padding px-6 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-12">
            <Play className="w-8 h-8 text-terracota" />
            <h2 className="font-serif text-4xl font-bold text-stone-900">{features.watch}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {watchContent.map((item, idx) => (
              <div key={idx} className="group cursor-pointer">
                <div className="relative aspect-video overflow-hidden mb-4 rounded-lg">
                  <img
                    src={item.thumbnail}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                      <Play className="w-6 h-6 text-stone-900 ml-1" />
                    </div>
                  </div>
                  <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {item.duration}
                  </span>
                </div>
                <h3 className="font-serif text-xl font-bold text-stone-900 mb-2 group-hover:text-terracota transition-colors">
                  {item.title}
                </h3>
                <p className="text-sm text-stone-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Read Section */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-12">
            <BookOpen className="w-8 h-8 text-terracota" />
            <h2 className="font-serif text-4xl font-bold text-stone-900">{features.read}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {readContent.map((item, idx) => (
              <div key={idx} className="bg-white p-8 rounded-xl group cursor-pointer hover:shadow-lg transition-shadow">
                <span className="text-xs text-terracota font-semibold tracking-wider uppercase mb-3 block">
                  {item.category}
                </span>
                <h3 className="font-serif text-2xl font-bold text-stone-900 mb-3 group-hover:text-terracota transition-colors">
                  {item.title}
                </h3>
                <p className="text-stone-600 mb-4 leading-relaxed">{item.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-500">{item.readTime}</span>
                  <ArrowRight className="w-5 h-5 text-terracota group-hover:translate-x-2 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured In - Press Section */}
      <section className="section-padding px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">
            {language === 'es' ? 'Apariciones' : 'Featured In'}
          </h2>
          <div className="space-y-8">
            {pressFeatures.map((item, idx) => (
              <a 
                key={idx} 
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (item.url === '#') {
                    e.preventDefault();
                    window.open('https://google.com', '_blank');
                  }
                }}
                className="bg-vainilla p-8 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer"
                style={{ display: 'flex' }}
              >
                <div>
                  <span className="text-xs text-terracota font-semibold tracking-wider uppercase">
                    {item.type} - {item.date}
                  </span>
                  <h3 className="font-serif text-xl font-bold text-stone-900 mt-2">{item.title}</h3>
                  <p className="text-stone-600 mt-1">{item.publication}</p>
                </div>
                <ExternalLink className="w-5 h-5 text-terracota flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding px-6 bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl font-bold mb-6">
            {features.newsletter.title}
          </h2>
          <p className="text-lg mb-8 opacity-90">
            {features.newsletter.description}
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder={features.newsletter.placeholder}
              className="flex-1 px-4 py-3 text-stone-900 rounded-full focus:outline-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="features-newsletter-email"
            />
            <Button 
              type="submit"
              className="bg-terracota text-white hover:bg-white hover:text-stone-900 px-8 py-3 rounded-full"
              disabled={subscribing}
              data-testid="features-newsletter-btn"
            >
              {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : features.newsletter.button}
            </Button>
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding px-6 bg-cream">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl font-bold text-stone-900 mb-6">
            {features.cta.title}
          </h2>
          <Link to="/contact">
            <Button className="bg-terracota text-white hover:bg-stone-900 px-12 py-6 text-lg rounded-full">
              {features.cta.button}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Features;
